/**
 * Runtime default fetcher.
 *
 * Used as the FetcherDispatcher's terminal fallback when no foundation
 * transport claims the request. Sites that declare no transport at all —
 * starter/docs/marketing templates hitting /data/*.json — ride on this path
 * with zero config, and so does a site a host serves live.
 *
 * ⭐ It speaks exactly TWO lanes, and takes NO site-level vocabulary for a
 * backend of the author's own:
 *
 *   - a compiled file — `path:` under the site's base (`/data/<query>.json`,
 *     a per-record file), or a plain JSON `url:` the author wrote;
 *   - the host's RECORDS SERVICE — `ask:`, one POST per tick carrying every
 *     question the page asked, answered per key (the records contract, as this
 *     client reads it).
 *
 * A query's set — `scope:` / `where:` / `sort:` / `limit:` — and the `narrow`
 * a fetch takes of it are evaluated HERE, locally, over what the first lane
 * returns — with `@uniweb/core`'s one evaluator (`evaluateQuery`), the same the
 * build uses — and at the source when asked. Nothing decides that per site: the
 * LANE decides.
 *
 * ⛔ RETIRED 2026-09-04 [Diego]: `fetcher.baseUrl`, `headers`, `envelope`,
 * `supports`, `request.style` / `request.rename` and the `json-body`
 * request-style registry. *"3rd party endpoints must be supported at the
 * foundation level… making the runtime+core lean."* A backend with its own
 * base, headers, wire or query language is a TRANSPORT — a named
 * `{ resolve, cacheKey? }` the foundation (or an extension) registers and
 * the site selects per binding key (`as`) in `fetcher.transports`. The build warns once
 * and drops a retired key from the payload, so an author's backend does not
 * silently stop being reached.
 *
 * A request may carry `method: 'POST'` + `body:` for an endpoint that takes a
 * query in a body (GraphQL, a search endpoint) — an external query's, or its
 * `record:` request's — and `transform:`, a dot-path to the records in ONE
 * response. `{paramName}` placeholders in body strings are substituted from
 * `request.dynamicContext`, so a parametric page's record request can reference
 * its route param. ⛔ `envelope` (the object form of `detail:`) is retired with
 * it, 2026-09-13: a record request's own `transform` is `record.transform`.
 *
 * Exported from a subpath — `@uniweb/runtime/default-fetcher` — for
 * runtime-level callers (the editor's preview iframe, custom runtime
 * harnesses). **Foundations should not import this.** A foundation that
 * wants plain URL + JSON behavior simply omits its own transport; the
 * runtime installs this one automatically.
 *
 * Intentional omission: credentials / secrets. Any value the framework puts
 * into the served HTML is public to the browser. Sites needing private
 * credentials use a deployment-layer proxy — the site fetches a same-origin
 * URL, and a layer in front resolves the credential and forwards upstream.
 */

import {
  substitutePlaceholders,
  evaluateQuery,
  sortRecords,
  sortToWire,
  deriveCacheKey,
  resolveServiceUrl,
} from '@uniweb/core'

/**
 * @param {Object} [options]
 * @param {string} [options.basePath=''] - Prepended to local absolute paths
 *   for subpath deployments. Remote URLs pass through unchanged.
 * @param {boolean} [options.dev=false] - Dev-mode diagnostics: a bad `sort:`
 *   throws instead of delivering the records unsorted.
 * @param {Function|null} [options.fetch=null] - The transport. A host executing
 *   fetches outside a browser (an SSR isolate) decides how a site-relative
 *   address is dispatched — through its own origin or a service binding — and
 *   hands that in. Defaults to the global `fetch`, resolved at call time so a
 *   test stub installed later is honoured.
 * @returns {{ cacheKey: (req: Object) => string, resolve: (req: Object, ctx: Object) => Promise<{ data, error?, meta? }> }}
 */
export function createDefaultFetcher({ basePath = '', dev = false, fetch: fetchImpl = null } = {}) {
  const doFetch = (input, init) => (fetchImpl || globalThis.fetch)(input, init)
  const pathPrefix = basePath && basePath !== '/' ? basePath.replace(/\/$/, '') : ''


  // ⭐ THE RECORDS SERVICE — a batch of the misses, one POST, merged per key.
  //
  // The entity store dispatches every config a page needs in one synchronous
  // loop before awaiting any of them, so a question enqueued here and flushed
  // on the next microtask carries every miss of that page in one body. The
  // batch response is never cached as one: each request gets its own answer,
  // keyed by its own question.
  const askQueues = new Map()
  const askRecords = (request, ctx) => {
    // ⛔ A question needs the query's Model ref. A payload that offers the
    // service and carries no `config.queries` entry for the query cannot ask;
    // that is a producer defect and it is said here, per key, with no request.
    if (typeof request.schema !== 'string' || !request.schema) {
      return Promise.resolve({
        data: null,
        error: `the payload offers the records service but carries no Model ref for query ` +
          `"${request.query ?? request.as}" (config.queries) — it cannot be asked`,
      })
    }
    return new Promise((resolve) => {
      const url = resolveServiceUrl(request.ask, pathPrefix)
      let queue = askQueues.get(url)
      if (!queue) {
        queue = []
        askQueues.set(url, queue)
        queueMicrotask(() => {
          askQueues.delete(url)
          flushAsked(url, queue, doFetch)
        })
      }
      queue.push({ request, ctx, resolve })
    })
  }

  // ⭐ VIEWS OF ONE FILE SHARE ONE READ IN FLIGHT. Each view — its own `scope`,
  // `where`, `sort` or `limit` — is its own cache entry (`deriveCacheKey` hashes
  // the view) and is cut from the read; views asked together (a page's sections,
  // a host's prefetch) share one request. ⛔ Until 2026-09-11 the view was applied
  // before the dispatcher stored the answer under a key that left the view out, so
  // one file had ONE entry and whoever asked first decided what everyone got
  // (measured). A settled read is not kept: a view asked later reads again (a
  // static host's HTTP cache answers it), so dropping an entry and asking again
  // still reaches the source. The read carries no caller's abort signal — several
  // views may be waiting on it, and one navigating away must not cancel it for
  // the others.
  const reads = new Map()
  const readOnce = (target, init) => {
    const key = `${init.method} ${target} ${init.body ?? ''}`
    let pending = reads.get(key)
    if (!pending) {
      pending = readResponse(doFetch, target, init).finally(() => reads.delete(key))
      reads.set(key, pending)
    }
    return pending
  }

  return {
    /**
     * The cache identity is the request's ADDRESS and the view it takes of it —
     * or, when asked of the records service, the QUESTION (`deriveCacheKey`).
     * Two pages declaring different `where:` clauses against one path are two
     * entries, each cut from a read of the file (`readOnce`, above).
     */
    cacheKey(request) {
      return deriveCacheKey(request)
    },

    async resolve(request, ctx = {}) {
      if (!request) return { data: null }
      if (request.ask) return askRecords(request, ctx)
      const { path, url, transform, body: rawBody } = request

      // Normalize method. Only GET and POST are supported by the default
      // fetcher — mutations (PUT/PATCH/DELETE) are a different feature
      // (optimistic updates, action semantics) and don't belong here.
      let method = (request.method || 'GET').toUpperCase()
      if (method !== 'GET' && method !== 'POST') {
        console.warn(`[default-fetcher] method "${request.method}" is not supported — falling back to GET.`)
        method = 'GET'
      }

      let target
      if (path) {
        // Local file under public/ — basePath applies for subpath deploys.
        target = pathPrefix && path.startsWith('/') && !path.startsWith('//')
          ? pathPrefix + path
          : path
      } else if (url) {
        // A URL the author wrote, sent exactly as written.
        target = url
      } else {
        return { data: [], error: 'No path, url or ask specified' }
      }

      const init = { method }

      if (method === 'POST') {
        // Substitute {paramName} placeholders in body strings using the
        // dynamic-route context. The helper expects a flat key→value map;
        // build it from dynamicContext's { paramName, paramValue } shape.
        // Strict-brace matcher: GraphQL selection sets pass through unchanged.
        const dc = request.dynamicContext
        const body = (rawBody !== undefined && rawBody !== null && dc && dc.paramName)
          ? substitutePlaceholders(rawBody, { [dc.paramName]: dc.paramValue }, { encode: false })
          : rawBody
        if (body !== undefined && body !== null) {
          init.headers = { 'Content-Type': 'application/json' }
          init.body = typeof body === 'string' ? body : JSON.stringify(body)
        }
      }

      try {
        const response = await readOnce(target, init)
        if (ctx.signal?.aborted) return { data: [], error: 'aborted' }

        if (!response.ok) {
          return { data: [], error: `HTTP ${response.status}: ${response.statusText}` }
        }

        let data = response.body

        // Unwrap the response: the request's own `transform:`, and nothing else — a
        // record request carries its `record.transform`, never the list's.
        if (transform && data !== null && data !== undefined) {
          data = getNestedValue(data, transform)
        }

        // Evaluate the query locally. Only applies to array data
        // (filtering/sorting/limiting a single record doesn't make sense).
        // For non-arrays, operators are ignored — the source returned what
        // it returned.
        data = applyOperators(data, request, { dev, locale: sortLocale(request, ctx) })

        // ⭐ Say whether WHOLE records were delivered, so the record index can
        // file them — what the config asked for, echoed: briefs when the query
        // has a per-record source, whole records otherwise. (The service reports
        // `whole` per
        // key and overrides this with what it actually served.)
        const whole = typeof request.whole === 'boolean' ? request.whole : undefined
        return whole === undefined ? { data: data ?? [] } : { data: data ?? [], meta: { whole } }
      } catch (error) {
        if (error?.name === 'AbortError') {
          return { data: [], error: 'aborted' }
        }
        return { data: [], error: error?.message || String(error) }
      }
    },
  }
}

/**
 * One question of a batch, in the records service's own vocabulary
 * (the records contract, §2).
 * ⭐ TWO LEVELS, ruled 2026-09-14 [Diego]:
 *
 *   - **the top level is the query as saved** — `schema` (required), `scope` a bare
 *     path, `where`, `sort` one key spelled `date` / `-date`, `limit` — and `whole`
 *     when the whole record is wanted. Its records are the set, and the top level is
 *     the same on every question about it;
 *   - **`narrow`** — how this page takes less than the set: `where`, `match` (a
 *     parametric page's record), `sort`, `cursor` (to resume a previous answer) and
 *     `limit`. Absent when the question asks for the set as it is.
 *
 * ⛔ Until 2026-09-14 the two were one flat question — the page's `where` joined the
 * query's, its `sort` and `limit` replaced the query's — and `match` and `cursor` sat
 * at the top level, where the service no longer takes them. There is no fallback:
 * the service answers an unknown or misplaced field with a `400` for the request.
 *
 * ⭐ A where-object crosses exactly as authored: the evaluator and the service speak
 * one language (`@uniweb/core`'s where evaluator). ⛔ `nin` was respelled `not_in`
 * here until 2026-09-13, when the evaluator took `not_in` as its only spelling.
 * Anything the service does not answer (a retired operator) is sent as written and
 * answered there: never approximated.
 */
function toQuestion(request) {
  const q = { schema: request.schema }
  const where = request.where && typeof request.where === 'object' ? request.where : null
  // ⭐ `scope` crosses as authored. ⛔ A top-level `where.path.under` was respelled
  // into it until 2026-09-11, when that form was retired in favour of `scope:`
  // [Diego] — the build refuses it now, so there is nothing left to respell.
  const scope = typeof request.scope === 'string' && request.scope ? request.scope : null
  if (scope) q.scope = scope
  if (where) q.where = where
  const sort = wireSort(request.sort)
  if (sort) q.sort = sort
  if (typeof request.limit === 'number' && request.limit > 0) q.limit = request.limit
  // ⛔ ONLY WHEN TRUE. The brief is the default and absent means the brief, so
  // sending `whole: false` would be noise on every list question the client makes.
  if (request.whole === true) q.whole = true
  const narrow = toNarrow(request.narrow)
  if (narrow) q.narrow = narrow
  // ⛔ `exhaustive` and `maxPages` deliberately do NOT cross — they are client
  // instructions about how many times to ask, never part of what is being asked.
  return q
}

/**
 * A question's `narrow`, with only the fields the service takes inside it — any other
 * is a `400` for the whole request there — or `null` when nothing is left.
 *
 *   - `where` crosses as authored;
 *   - `match` — the record of a parametric page: one key and the URL's value, beside
 *     the author's `where`, never merged into it (`buildDetailConfig`);
 *   - `sort` in the service's spelling;
 *   - ⭐ `cursor` — the ONLY field in a question that is not the author's: opaque, from a
 *     previous answer's `cursors`, sent back on the same question and nothing else
 *     changed. A cursor is a narrowing like the rest, so the top level stays the query as
 *     saved on every page;
 *   - `limit`, when it cuts.
 */
function toNarrow(narrow) {
  if (!narrow || typeof narrow !== 'object') return null
  const n = {}
  if (narrow.where && typeof narrow.where === 'object') n.where = narrow.where
  if (narrow.match && typeof narrow.match === 'object') n.match = narrow.match
  const sort = wireSort(narrow.sort)
  if (sort) n.sort = sort
  if (typeof narrow.cursor === 'string' && narrow.cursor) n.cursor = narrow.cursor
  if (typeof narrow.limit === 'number' && narrow.limit > 0) n.limit = narrow.limit
  return Object.keys(n).length > 0 ? n : null
}

/**
 * A `sort` in the service's spelling — `date` / `-date` — or, for one outside the
 * language, the author's text AS WRITTEN, which the service answers by its own rule
 * (ignored: the author's arrangement). ⛔ Never a throw: this runs while a page's whole
 * batch is being composed, before any request, and until 2026-09-14 a sort `parseSort`
 * refuses — two keys, a bad direction — threw here and left every question of the batch
 * pending forever.
 */
function wireSort(sort) {
  try {
    return sortToWire(sort)
  } catch {
    return typeof sort === 'string' && sort.trim() ? sort : null
  }
}

/**
 * Send one batch to the records service and hand each question its own answer.
 *
 * The response is `{ data, whole?, cursors?, partial? }` (backend's records-query
 * contract rev D, 2026-09-13), each map absent when empty: `data` answers exactly the
 * keys sent — `[]` when nothing matched, and `[]` too for a question an author got
 * wrong, since the service answers a mistake rather than refusing it; `whole[key]` says
 * a key was served as whole records; `cursors[key]` that more records remain past the
 * question's `narrow.limit` — a query's own `limit` defines the set and never produces
 * one; `partial[key]` that a source of the key did not answer. A key
 * missing from `data` is a protocol violation and is reported as an error, never as
 * silence. ⛔ `errors` and `limits` are not read: the service stopped sending them in
 * the same revision, and this client read both until 2026-09-14.
 *
 * ⭐ `cursors` ARE READ — 2026-09-06 [Diego], reversing the ruling that had them
 * "received and IGNORED, because framework has no paging concept." ⛔ **That ruling
 * described our client and was silently wrong about our USERS:** a `cursors` entry is
 * how the service says there is more, and discarding it meant a hosted list could
 * render part of its records with no way for an author to tell that from the end of
 * the data. **The silent class, on a visitor's page.**
 *
 * Two behaviours, deliberately not one:
 *
 *   - **a page render REPORTS** — `meta.partial` rides the answer, and nothing pages
 *     automatically. Auto-paging here would put unbounded round trips in front of
 *     paint for a section that may only show ten rows.
 *   - **an exhaustive caller PAGES** — `request.exhaustive` follows `cursors`
 *     until the service stops issuing them. `collectSiteRecords` is the caller
 *     that wants it (a corpus is not a page), and `maxPages` bounds the loop so
 *     a service that always answers with a cursor cannot spin.
 */

/**
 * Pages an exhaustive request follows before stopping and reporting the answer
 * as partial. A caller sets its own with `request.maxPages`.
 *
 * ⚖️ **20 is a bound, not a target** — it is the value a real caller chose for a
 * real per-request budget (a search index, 20 × 100), taken as the default
 * because any bound prevents a runaway and a low one fails visibly rather than
 * expensively. A caller that knows its budget passes its own.
 */
const DEFAULT_MAX_PAGES = 20
async function flushAsked(url, queue, doFetch) {
  // One shared page loop: the batch is sent, and any entry that asked to be
  // exhaustive and came back with a cursor is re-sent alone until it is done.
  const pending = new Map()
  const body = {}
  const keys = []
  for (const entry of queue) {
    const base = entry.request.as || 'q'
    let key = base
    for (let n = 2; key in body; n += 1) key = `${base}#${n}`
    keys.push(key)
    body[key] = toQuestion(entry.request)
  }
  let parsed
  try {
    const response = await doFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      // A protocol violation is refused for the WHOLE request with a problem body
      // whose `detail` names the key and the fault (an unknown operator, an empty
      // binding key, a non-BCP-47 locale segment…). Surface that sentence on every
      // key of the batch rather than the bare status: the author reads
      // `block.dataError` and the status alone says nothing they can act on.
      let detail = null
      try {
        const problem = safeParseJSON(await response.text())
        if (problem && typeof problem.detail === 'string' && problem.detail) detail = problem.detail
      } catch { /* an unreadable body falls back to the status line */ }
      const error = detail
        ? `HTTP ${response.status}: ${detail}`
        : `HTTP ${response.status}: ${response.statusText}`
      for (const entry of queue) entry.resolve({ data: null, error })
      return
    }
    parsed = await response.json()
  } catch (error) {
    const message = error?.name === 'AbortError' ? 'aborted' : (error?.message || String(error))
    // ⛔ AN ABORT KEEPS WHAT ARRIVED. On an exhaustive walk every in-flight key
    // fails at once here, so discarding held pages loses the whole corpus rather
    // than one key — the "rejection that loses every key" a caller cannot have.
    for (const entry of queue) {
      const held = Array.isArray(entry.collected) && entry.collected.length ? entry.collected : null
      entry.resolve(held
        ? { data: held, error: message, meta: withMeta({ partial: true, pages: entry.page }) }
        : { data: null, error: message })
    }
    return
  }
  const data = parsed && typeof parsed.data === 'object' && parsed.data ? parsed.data : {}
  // ⭐ `whole[key]` is present only for keys delivered as WHOLE entities — so a
  // key's ABSENCE is the brief, and a key present that never asked is the
  // brief-less Model saying so. Absent entirely when every key is briefs.
  const wholes = parsed && typeof parsed.whole === 'object' && parsed.whole ? parsed.whole : {}
  const cursors = parsed && typeof parsed.cursors === 'object' && parsed.cursors ? parsed.cursors : {}
  // ⭐ `partial[key]` — `{ source, code, detail }`: a source of that key did not answer,
  // and the key carries what the others returned. Unread, a degraded key looked complete.
  const degraded = parsed && typeof parsed.partial === 'object' && parsed.partial ? parsed.partial : {}
  queue.forEach((entry, i) => {
    const key = keys[i]
    // ⛔ A FAILURE MUST NOT DISCARD PAGES ALREADY COLLECTED. An exhaustive walk
    // that fails on page 7 has six pages in hand, and a caller that asked for a
    // corpus would rather have them marked partial than lose the key — losing it
    // is indistinguishable from "this query has no records".
    const held = Array.isArray(entry.collected) && entry.collected.length ? entry.collected : null
    if (!(key in data)) {
      const error = `the records service answered without the key "${key}"`
      entry.resolve(held
        ? { data: held, error, meta: withMeta({ partial: true, pages: entry.page }) }
        : { data: null, error })
      return
    }
    const whole = typeof wholes[key] === 'boolean'
      ? wholes[key]
      : (typeof entry.request.whole === 'boolean' ? entry.request.whole : undefined)

    const cursor = typeof cursors[key] === 'string' && cursors[key] ? cursors[key] : null
    const rows = Array.isArray(data[key]) ? data[key] : data[key]

    // An exhaustive caller collects the page and asks for the next one.
    if (cursor && entry.request.exhaustive && Array.isArray(rows)) {
      const acc = entry.collected ? entry.collected.concat(rows) : rows.slice()
      const page = (entry.page || 1) + 1
      const cap = typeof entry.request.maxPages === 'number' && entry.request.maxPages > 0
        ? entry.request.maxPages
        : DEFAULT_MAX_PAGES
      if (page <= cap) {
        pending.set(entry, { cursor, collected: acc, page, whole })
        return
      }
      // The caller's own bound, not the service's: report rather than spin.
      entry.resolve({ data: acc, meta: withMeta({ whole, partial: true, pages: cap }) })
      return
    }

    const collected = entry.collected ? entry.collected.concat(Array.isArray(rows) ? rows : []) : rows
    const meta = withMeta({
      whole,
      // ⭐ ONE FLAG, MEANING **NOT THE WHOLE POPULATION** — asked for by name, so
      // a caller has one boolean to branch on rather than several signals to
      // combine. It is set in every case that means it:
      //   · a cursor came back and this caller does not page (a page render);
      //   · a cursor came back and the caller's `maxPages` stopped the walk;
      //   · the walk failed or aborted with pages already in hand;
      //   · a source of the key did not answer (`partial[key]`), and `unavailable`
      //     carries the service's own account of which one.
      // ⛔ Named `truncated` when it shipped in 0.17.0; renamed the same day, before
      // any consumer adopted it, because "truncated" says something was cut and this
      // also means "there is more you did not ask for".
      partial: (cursor || key in degraded) ? true : undefined,
      unavailable: key in degraded ? degraded[key] : undefined,
      pages: entry.page && entry.page > 1 ? entry.page : undefined,
    })
    entry.resolve(meta ? { data: collected, meta } : { data: collected })
  })

  if (pending.size === 0) return
  // Re-ask each unfinished key on its own — the cursor is per key, so a batch
  // would have to correlate several independent positions through one body.
  // ⭐ The cursor rides inside `narrow` and NOTHING ELSE in the question changes: the
  // service resumes only the question a cursor came from, and answers a cursor sent
  // with anything else changed with a `400`.
  await Promise.all([...pending].map(([entry, state]) => {
    const next = {
      ...entry,
      request: { ...entry.request, narrow: { ...(entry.request.narrow || {}), cursor: state.cursor } },
      collected: state.collected,
      page: state.page,
    }
    return flushAsked(url, [next], doFetch)
  }))
}

/**
 * Read one response and parse its body — the part of a fetch every view of an
 * address shares. A JSON body is parsed as JSON; anything else is tried as JSON
 * and kept as text when it is not. A failed response keeps its text, so a caller
 * can read a message out of it (the records service's problem details).
 */
async function readResponse(doFetch, target, init) {
  const response = await doFetch(target, init)
  if (!response.ok) {
    let text = null
    try {
      text = await response.text()
    } catch {
      // Body not readable — the status line is all there is.
    }
    return { ok: false, status: response.status, statusText: response.statusText, text }
  }
  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) return { ok: true, body: await response.json() }
  const text = await response.text()
  try {
    return { ok: true, body: JSON.parse(text) }
  } catch {
    return { ok: true, body: text }
  }
}

/** Build a `meta` from the fields that are actually present, or `undefined`. */
function withMeta(fields) {
  const out = {}
  for (const [k, v] of Object.entries(fields)) if (v !== undefined) out[k] = v
  return Object.keys(out).length ? out : undefined
}

/**
 * The locale a list's texts are collated in: the page's (ruled 2026-09-14 [Diego]).
 * A request asked in a locale carries it; otherwise the caller's — the website's
 * active locale in the browser, the locale a host's prerender renders.
 */
function sortLocale(request, ctx) {
  if (typeof request?.locale === 'string' && request.locale) return request.locale
  if (typeof ctx?.locale === 'string' && ctx.locale) return ctx.locale
  const active = ctx?.website?.getActiveLocale?.()
  return typeof active === 'string' && active ? active : null
}

/**
 * Evaluate the query over what the source returned — the ONE evaluator,
 * `@uniweb/core`'s `evaluateQuery`, so the browser orders and filters exactly as
 * the build does: the query's set first (`scope` over each record's placement,
 * `where`, `sort`, `limit`), then the fetch's `narrow` of it. Only the sort is this
 * lane's own, for what it does with a bad `sort:` in production.
 */
function applyOperators(data, request, { dev = false, locale = null } = {}) {
  return evaluateQuery(data, request, {
    locale,
    sort: (items, sortExpr, options) => applySort(items, sortExpr, dev, options.locale),
  })
}

/**
 * ⛔ This was a second sort implementation until 2026-09-04, and it honoured
 * a comma-separated MULTI-KEY sort the language does not have (single-key by
 * ruling). A bad `sort:` is an authoring error: dev throws so it is seen; in
 * production the records are delivered in source order and the reason is
 * logged once — a wrong order is not worth a broken page for a visitor.
 */
const warnedBadSorts = new Set()
function applySort(items, sortExpr, dev, locale) {
  try {
    return sortRecords(items, sortExpr, { locale })
  } catch (err) {
    if (dev) throw err
    const key = String(sortExpr)
    if (!warnedBadSorts.has(key)) {
      warnedBadSorts.add(key)
      console.error(`[default-fetcher] ${err.message} Records delivered unsorted.`)
    }
    return items
  }
}

/**
 * Walk a dotted path into an object. Missing segments short-circuit to
 * `undefined` so callers can distinguish "present and empty" from "not there."
 */
function getNestedValue(obj, path) {
  if (!obj || !path) return obj
  let current = obj
  for (const part of path.split('.')) {
    if (current === null || current === undefined) return undefined
    current = current[part]
  }
  return current
}

/**
 * JSON.parse that returns `undefined` on failure instead of throwing.
 * Used when we want to probe a response body for an error path but don't
 * want a non-JSON body to surface as a parser exception.
 */
function safeParseJSON(text) {
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}
